import EstadoSustentacaoOral, { ESTADO_SUSTENTACAO_ORAL } from '@/dominio/EstadoSustentacaoOral';
import ModoParticipacaoAdvogado, { MODO_PARTICIPACAO_ADVOGADO } from '@/dominio/ModoParticipacaoAdvogado';
import SituacaoInscricaoAdvogado, { SITUACAO_INSCRICAO_ADVOGADO } from '@/dominio/SituacaoInscricaoAdvogado';
import SystemUtil from '@/util/SystemUtil';
import { faFileExport } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Close } from '@mui/icons-material';
import Group from '@mui/icons-material/Group';
import Warning from '@mui/icons-material/Warning';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Paper,
    Radio,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import { yellow } from '@mui/material/colors';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import '../../css/botao.css';
import ReactComponent from '../ReactComponent';
import IconeAdvogadoModoParticipacao from './icones/IconeAdvogadoModoParticipacao';
import IconeAdvogadoPreferencia from './icones/IconeAdvogadoPreferencia';

/**
 * Separador CSV para o arquivo a ser exportado.
 * @readonly
 * @constant {string}
 */
const SEPARADOR_CSV = ';';

/**
 * Estilo da borda das células da tabela.
 * @readonly
 * @constant {string}
 */
const ESTILO_BORDA = '1px solid rgba(224, 224, 224, 1)';

/**
 * Largura da coluna de nome do advogado.
 * @readonly
 * @constant {number|string}
 */
const LARGURA_COLUNA_NOME_ADVOGADO = 350;

/**
 * Largura das células de presença.
 * @readonly
 * @constant {number|string}
 */
const LARGURA_COLUNAS_PRESENCA = 70;

/**
 * Largura das células de modo de participação.
 * @readonly
 * @constant {number|string}
 */
const LARGURA_COLUNAS_MODOS_PARTICIPACAO = 95;

/**
 * Largura das células de presença na tabela de listagem unitária de inscrições.
 * @readonly
 * @constant {number|string}
 */
const LARGURA_COLUNAS_PRESENCA_VISAO_UNITARIA = 280;

/**
 * Largura das células de modo de participação na tabela de listagem unitária de inscrições.
 * @readonly
 * @constant {number|string}
 */
const LARGURA_COLUNAS_MODOS_PARTICIPACAO_VISAO_UNITARIA = 450;

/**
 * Largura das células de estado da sustentação oral.
 * @readonly
 * @constant {number|string}
 */
const LARGURA_COLUNAS_ESTADO_SUSTENTACAO = 90;

/**
 * Tamanho da fonte das colunas de controle (presença, participação, estado).
 * @readonly
 * @constant {number|string}
 */
const FONTE_COLUNAS_CONTROLE = '12px';

class ModalControleAdvogados extends ReactComponent {
    aberto = false;
    visualizacaoAgrupada = true;
    /** @type {Map | undefined} */ advogadosComProcessos = undefined;

    /** @desc Maior quantidade de processos de um único advogado (importante para definir, dinamicamente, quantas colunas de sequenciais de preferencias serão necessárias no CSV exportado) */
    qtdColunasInscricoesAdvogado = 0;

    /** @desc Dados a serem exportados. */
    dados;

    /** @desc Controle de geração do arquivo. */
    gerandoArquivo = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            aberto: observable,
            visualizacaoAgrupada: observable,
            advogadosComProcessos: observable,
            qtdColunasInscricoesAdvogado: observable,
            dados: observable,
            gerandoArquivo: observable,
            handleClickExportarLista: action,
            conteudoArquivoCsv: computed,
            nomeArquivoCsv: computed,
            abrir: action,
            inscricoes: computed,
            advogados: computed,
            fechar: action,
            sessao: computed,
            handleChangePresenca: action,
            handleChangeParticipacao: action,
            handleChangeEstadoSustentacaoOral: action,
            handleChangeAdvogado: action,
            handleChangePresencaUnitaria: action,
            handleChangeParticipacaoUnitaria: action,
            handleChangeEstadoSustentacaoOralUnitaria: action,
            handleChangeAdvogadoUnitario: action,
            atualizarAdvogadoProcessoENotificar: action,
            gerarDadosExportacao: action,
            handleChangeVisualizacaoAgrupada: action,
            cabecalhos: computed
        });
    }

    handleClickExportarLista = () => {
        this.gerandoArquivo = true;
        this.dados = this.gerarDadosExportacao();
        SystemUtil.exportarAquivoCsv(this.nomeArquivoCsv, this.conteudoArquivoCsv);
        this.gerandoArquivo = false;
    };

    get conteudoArquivoCsv() {
        const cabecalhoCSV = this.cabecalhos.map((h) => h.label).join(SEPARADOR_CSV);
        const linhasCSV = this.dados.map((item) =>
            this.cabecalhos
                .map((h) => {
                    const value = item[h.key];
                    return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : (value ?? '');
                })
                .join(SEPARADOR_CSV)
        );
        return [cabecalhoCSV, ...linhasCSV].join('\n');
    }

    /**
     * Monta e retorna o nome do arquivo CSV.
     *
     * @returns {string} O nome do arquivo CSV.
     */
    get nomeArquivoCsv() {
        return this.sessaoStore?.montarNomeArquivoExportacao('advogados');
    }

    abrir = () => {
        this.aberto = true;
        this.advogadosComProcessos = this.salaSessaoStore.representantesComInscricaoParaSessaoPresencialComProcessos;

        if (this.advogados.length === 0 && this.aberto) {
            this.mensagemStore.abrirMensagemAlerta(
                'Não existem inscrições de advogados ou pedidos de preferência cadastrados na sessão.'
            );
        }
    };

    get inscricoes() {
        return this.salaSessaoStore.inscricoesAdvogadosSustentacaoOral;
    }

    get advogados() {
        return this.salaSessaoStore.advogadosSustentacaoOral;
    }

    fechar = () => {
        this.aberto = false;
    };

    get sessao() {
        return this.sessaoStore.sessaoSelecionada;
    }

    handleChangePresenca = async (value, index) => {
        const atributo = 'presente';
        await this.handleChangeAdvogado(value, index, atributo);
    };

    handleChangeParticipacao = async (value, index) => {
        const atributo = 'modoParticipacao';
        await this.handleChangeAdvogado(value, index, atributo);
    };

    handleChangeEstadoSustentacaoOral = (value, index) => {
        const atributo = 'estadoSustentacaoOral';
        const estadoDto = { id: value, descricao: EstadoSustentacaoOral.getDescricaoByCodigo(value) };
        this.handleChangeAdvogado(estadoDto, index, atributo);
    };

    handleChangeAdvogado = async (value, index, atributo) => {
        this.advogados[index][atributo] = value;

        const chaveAdvogado = this.salaSessaoStore.getRepresentanteKey(this.advogados[index]);
        // @ts-ignore
        const advogadoComProcessos = this.advogadosComProcessos?.get(chaveAdvogado);

        if (advogadoComProcessos !== undefined) {
            for (const inscricao of advogadoComProcessos.inscricoes) {
                if (!inscricao.idInscricao) {
                    throw new Error('inscricao.idInscricao não pode ser nulo');
                }

                inscricao[atributo] = value;

                const chaveInscricao = this.salaSessaoStore.getInscricaoKey(chaveAdvogado, inscricao);
                this.inscricoes[chaveInscricao][atributo] = value;

                if (atributo === 'presente') {
                    await this.advogadoSustentacaoOralService.alterarPresencaInscricao(inscricao.idInscricao, value);
                } else if (atributo === 'modoParticipacao') {
                    await this.advogadoSustentacaoOralService.alterarModoParticipacaoInscricao(
                        inscricao.idInscricao,
                        value
                    );
                }
            }
        }

        const processosNotificar = [];

        this.salaSessaoStore.processosComPedidoDeParticipacaoPresencial.forEach((registro) => {
            if (registro.pedidoSustentacaoOral?.pedidosDetalhados) {
                let encontrou = false;
                registro.pedidoSustentacaoOral.pedidosDetalhados.forEach((pedido) => {
                    if (pedido.advogados) {
                        pedido.advogados.forEach((adv) => {
                            if (adv.nome === this.advogados[index].nome) {
                                adv[atributo] = value;
                                encontrou = true;
                            }
                        });
                    }
                });

                if (encontrou && (atributo === 'presente' || atributo === 'modoParticipacao')) {
                    processosNotificar.push(registro);
                }
            }
        });

        for (const registro of processosNotificar) {
            await this.advogadoSustentacaoOralService.notificarAlteracaoAdvogado(
                registro.sessaoId,
                registro.processo.numeroCompleto,
                this.sessaoStore.clienteEnvelopeId
            );
        }
    };

    handleChangePresencaUnitaria = async (value, inscricao) => {
        if (!inscricao.idInscricao) {
            throw new Error('inscricao.idInscricao não pode ser nulo');
        }
        const atributo = 'presente';
        await this.advogadoSustentacaoOralService.alterarPresencaInscricao(inscricao.idInscricao, value);
        this.salaSessaoStore.alteraAtributoAdvogado(value, inscricao, atributo);
        this.atualizarAdvogadoProcessoENotificar(value, inscricao, atributo);
    };

    handleChangeParticipacaoUnitaria = async (value, inscricao) => {
        if (!inscricao.idInscricao) {
            throw new Error('inscricao.idInscricao não pode ser nulo');
        }
        const atributo = 'modoParticipacao';
        await this.advogadoSustentacaoOralService.alterarModoParticipacaoInscricao(inscricao.idInscricao, value);
        this.salaSessaoStore.alteraAtributoAdvogado(value, inscricao, atributo);
        this.atualizarAdvogadoProcessoENotificar(value, inscricao, atributo);
    };

    atualizarAdvogadoProcessoENotificar = async (value, inscricao, atributo) => {
        for (const registro of this.salaSessaoStore.processosComPedidoDeParticipacaoPresencial) {
            if (registro.pedidoSustentacaoOral?.pedidosDetalhados) {
                for (const pedido of registro.pedidoSustentacaoOral.pedidosDetalhados) {
                    if (pedido.advogados) {
                        for (const adv of pedido.advogados) {
                            if (
                                adv.nome === inscricao.advogado.nome &&
                                inscricao.sessaoId.split('---').includes(registro.sessaoId) &&
                                registro.processo.numeroCompleto === inscricao.numeroCompletoProcesso &&
                                pedido.codigoDaParte === inscricao.codigoDaParte
                            ) {
                                adv[atributo] = value;
                                await this.advogadoSustentacaoOralService.notificarAlteracaoAdvogado(
                                    registro.sessaoId,
                                    registro.processo.numeroCompleto,
                                    this.sessaoStore.clienteEnvelopeId
                                );
                                return;
                            }
                        }
                    }
                }
            }
        }
    };

    handleChangeEstadoSustentacaoOralUnitaria = (value, inscricao) => {
        const atributo = 'estadoSustentacaoOral';
        const estadoDto = { id: value, descricao: EstadoSustentacaoOral.getDescricaoByCodigo(value) };
        this.handleChangeAdvogadoUnitario(estadoDto, inscricao, atributo);
    };

    handleChangeAdvogadoUnitario = (value, inscricao, atributo) => {
        this.salaSessaoStore.processosComPedidoDeParticipacaoPresencial.forEach((registro) => {
            if (registro.pedidoSustentacaoOral?.pedidosDetalhados) {
                registro.pedidoSustentacaoOral.pedidosDetalhados.forEach((pedido) => {
                    if (pedido.advogados) {
                        pedido.advogados.forEach(async (adv) => {
                            if (
                                adv.nome === inscricao.advogado.nome &&
                                inscricao.sessaoId.split('---').includes(registro.sessaoId) &&
                                registro.processo.numeroCompleto === inscricao.numeroCompletoProcesso &&
                                pedido.codigoDaParte === inscricao.codigoDaParte
                            ) {
                                adv[atributo] = value;
                                const dto = {};
                                dto.advogado = {
                                    ...inscricao.advogado,
                                    preferencia: inscricao.preferencia,
                                    sustentacaoOral: inscricao.sustentacaoOral,
                                    presente: inscricao.presente,
                                    modoParticipacao: inscricao.modoParticipacao,
                                    sequencialPreferencia: inscricao.sequencialPreferencia,
                                    dataSolicitacaoSustentacao: inscricao.dataSolicitacaoSustentacao
                                };
                                dto.advogado[atributo] = value;
                                dto.codigoDaParte = pedido.codigoDaParte;
                                dto.denominacaoDaParte = pedido.denominacaoDaParte;
                                dto.nomeDaParte = pedido.nomeDaParte;

                                await this.processoService.registrarPresencaAdvogadoProcesso(
                                    registro.sessaoId,
                                    registro.processo.numeroCompleto,
                                    dto
                                );
                            }
                        });
                    }
                });
            }
        });

        const chaveAdvogado = this.salaSessaoStore.getRepresentanteKey(inscricao.advogado);
        // @ts-ignore
        const advogadoComProcessos = this.advogadosComProcessos?.get(chaveAdvogado);

        if (advogadoComProcessos !== undefined) {
            advogadoComProcessos.inscricoes.map(function (inscricaoStore) {
                if (
                    inscricaoStore.sessaoId === inscricao.sessaoId &&
                    inscricaoStore.numeroCompletoProcesso === inscricao.numeroCompletoProcesso &&
                    inscricaoStore.codigoDaParte === inscricao.codigoDaParte
                ) {
                    inscricao[atributo] = value;
                }
                return inscricao;
            });
        }
    };

    /**
     * @desc Monta os dados para exportação.
     */
    gerarDadosExportacao() {
        const listaAdvogados = [...this.salaSessaoStore.advogadosSustentacaoOral];
        const listaExportacao = [];
        let totalMaximoInscricoesPorAdvogado = 0;

        listaAdvogados.forEach((advogado) => {
            advogado.sequenciaisPreferencia = [];

            this.salaSessaoStore.processosComPedidoDeParticipacaoPresencial.forEach((proc) => {
                proc?.pedidoSustentacaoOral?.pedidosDetalhados?.forEach((pedido) => {
                    pedido?.advogados?.forEach(async (adv) => {
                        if (
                            adv.regOAB.numero === advogado.regOAB.numero &&
                            ((!adv.regOAB.letra && !advogado.regOAB.letra) ||
                                adv.regOAB.letra === advogado.regOAB.letra) &&
                            adv.regOAB.siglaUf === advogado.regOAB.siglaUf
                        ) {
                            advogado.sequenciaisPreferencia.push(
                                proc.informacaoProcessoSalaSessao.sequenciaPreferencia
                            );
                        }
                    });
                });
            });
        });

        listaAdvogados.forEach((adv) => {
            if (totalMaximoInscricoesPorAdvogado < adv.sequenciaisPreferencia.length) {
                totalMaximoInscricoesPorAdvogado = adv.sequenciaisPreferencia.length;
            }

            const chaveAdvogado = this.salaSessaoStore.getRepresentanteKey(adv);
            const { presencas, modosParticipacao } = this.salaSessaoStore.getPresencasModosParticipacao(chaveAdvogado);

            const situacoes = this.situacoesPorExtenso(presencas);
            const participacoes = this.modosParticipacaoPorExtenso(modosParticipacao);

            const estadoSustentacao = adv.estadoSustentacaoOral?.descricao || '';

            listaExportacao.push({
                nome: adv.nome,
                oab: ModalControleAdvogados.formatarOab(adv),
                totalProcessos: adv.sequenciaisPreferencia.length,
                situacao: situacoes,
                participacao: participacoes,
                estadoSustentacao: estadoSustentacao,
                sequenciaisPreferencia: adv.sequenciaisPreferencia
            });
        });

        const listaExportacaoFormatada = listaExportacao.map(function (adv) {
            const linha = {
                nome: adv.nome,
                oab: adv.oab,
                totalProcessos: adv.totalProcessos,
                situacao: adv.situacao,
                participacao: adv.participacao,
                estadoSustentacao: adv.estadoSustentacao
            };

            for (let index = 0; index < adv.sequenciaisPreferencia.length; index++) {
                const keySequencial = ModalControleAdvogados.montarChaveSeqPreferencia(index + 1);
                linha[keySequencial] = adv.sequenciaisPreferencia[index];
            }

            return linha;
        });

        runInAction(() => {
            this.qtdColunasInscricoesAdvogado = totalMaximoInscricoesPorAdvogado;
            //console.log(`advogados.csv - Dados preparados para exportação com o máximo de ${this.qtdColunasInscricoesAdvogado} inscrição(ões).`);
        });

        return listaExportacaoFormatada;
    }

    handleChangeVisualizacaoAgrupada = () => {
        this.visualizacaoAgrupada = !this.visualizacaoAgrupada;
    };

    static formatarSeqPreferencia(seqP) {
        const QTD_DIGITOS = 2;
        return String(seqP).padStart(QTD_DIGITOS, '0');
    }

    static montarChaveSeqPreferencia(seqP) {
        return 'sequencialPreferencia' + ModalControleAdvogados.formatarSeqPreferencia(seqP);
    }

    static formatarOab(adv) {
        if (adv?.regOAB) {
            return `${adv.regOAB.numero}${adv.regOAB.letra ? `-${adv.regOAB.letra}` : ''}/${adv.regOAB.siglaUf}`;
        }

        return '';
    }

    /**
     * @desc Monta os cabeçalhos do CSV.
     */
    get cabecalhos() {
        const cabecalhos = [];

        cabecalhos.push(
            { label: 'NOME DO(A) ADVOGADO(A)', key: 'nome' },
            { label: 'OAB/UF', key: 'oab' },
            { label: 'TOTAL DE PROCESSOS', key: 'totalProcessos' },
            { label: 'PRESENÇA', key: 'situacao' },
            { label: 'PARTICIPAÇÃO', key: 'participacao' },
            { label: 'ESTADO SUSTENTAÇÃO', key: 'estadoSustentacao' }
        );

        for (let index = 0; index < this.qtdColunasInscricoesAdvogado; index++) {
            const labelNumeroFormatado = 'SEQ P.' + ModalControleAdvogados.formatarSeqPreferencia(index + 1);
            const keySequencial = ModalControleAdvogados.montarChaveSeqPreferencia(index + 1);

            cabecalhos.push({
                label: labelNumeroFormatado,
                key: keySequencial
            });
        }

        //console.log(`advogados.csv - Cabeçalhos preparados para exportação com o máximo de ${this.qtdColunasInscricoesAdvogado} inscrição(ões).`);
        return cabecalhos;
    }

    renderTiposInscricoesAdvogado(advogado) {
        const chaveAdvogado = this.salaSessaoStore.getRepresentanteKey(advogado);
        const { presencas, modosParticipacao } = this.salaSessaoStore.getPresencasModosParticipacao(chaveAdvogado);
        const cor = this.calcularCorIcone(presencas);

        const getChaveParticipacao = function (chaveAdvogado, participacao, cor) {
            return `${chaveAdvogado}|${participacao}|${cor}`;
        };

        return (
            <>
                {advogado.preferencia && (
                    <IconeAdvogadoPreferencia style={{ marginLeft: '7px', verticalAlign: 'sub' }} />
                )}
                {modosParticipacao.size > 0 &&
                    Array.from(modosParticipacao).map((participacao) => {
                        return (
                            <IconeAdvogadoModoParticipacao
                                key={getChaveParticipacao(chaveAdvogado, participacao, cor)}
                                modoParticipacao={participacao}
                                style={{
                                    color: cor,
                                    fill: cor,
                                    marginLeft: '7px',
                                    verticalAlign: 'sub',
                                    height:
                                        participacao === MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.codigo
                                            ? '20px'
                                            : undefined
                                }}
                            />
                        );
                    })}
                {presencas.size > 1 && this.renderAlertaMaisDeUmaSituacao(presencas)}
                {modosParticipacao.size > 1 && this.renderAlertaMaisDeUmaParticipacao(modosParticipacao)}
            </>
        );
    }

    renderTipoInscricaoAdvogadoUnitario(advogado, presenca, modoParticipacao) {
        const chaveAdvogado = this.salaSessaoStore.getRepresentanteKey(advogado);
        const cor = SituacaoInscricaoAdvogado.getCorByCodigo(presenca);

        const getChaveParticipacao = function (chaveAdvogado, participacao, cor) {
            return `${chaveAdvogado}|${participacao}|${cor}`;
        };

        return (
            <>
                {advogado.preferencia && (
                    <IconeAdvogadoPreferencia style={{ marginLeft: '7px', verticalAlign: 'sub' }} />
                )}
                <IconeAdvogadoModoParticipacao
                    key={getChaveParticipacao(chaveAdvogado, modoParticipacao, cor)}
                    modoParticipacao={modoParticipacao}
                    style={{
                        color: cor,
                        fill: cor,
                        marginLeft: '7px',
                        verticalAlign: 'sub',
                        height: modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.codigo ? '20px' : undefined
                    }}
                />
            </>
        );
    }

    modosParticipacaoPorExtenso(modosParticipacao) {
        let modos = '';
        let total = 0;

        for (const modoParticipacao of modosParticipacao.values()) {
            modos += ModoParticipacaoAdvogado.getDescricaoByCodigo(modoParticipacao);

            if (++total !== modosParticipacao.size) {
                modos += '; ';
            }
        }

        return modos;
    }

    situacoesPorExtenso(presencas) {
        let situacoes = '';
        let total = 0;

        for (const situacao of presencas.values()) {
            situacoes += SituacaoInscricaoAdvogado.getDescricaoPresencaCurtaByCodigo(situacao);

            if (++total !== presencas.size) {
                situacoes += '; ';
            }
        }

        return situacoes;
    }

    calcularCorIcone(presencas) {
        const corMaisDeUm = yellow[800];

        if (presencas.size === 0) {
            return SITUACAO_INSCRICAO_ADVOGADO.INSCRITO.cor;
        }

        if (presencas.size === 1) {
            const valor = presencas.values().next().value;
            return SituacaoInscricaoAdvogado.getCorByCodigo(valor);
        }

        return corMaisDeUm;
    }

    renderAlertaMaisDeUmaSituacao(situacoes) {
        const situacoesStr = this.situacoesPorExtenso(situacoes);

        return (
            <Tooltip
                title={`Atenção! Advogado com ${situacoes.size} informações de presença diferentes na sessão: ${situacoesStr}.`}
                placement="top"
            >
                <Warning style={{ fill: 'darkorange', marginLeft: '7px', verticalAlign: 'sub', fontSize: '22px' }} />
            </Tooltip>
        );
    }

    renderAlertaMaisDeUmaParticipacao(modosParticipacao) {
        const modosParticipacaoStr = this.modosParticipacaoPorExtenso(modosParticipacao);

        return (
            <Tooltip
                title={`Atenção! Advogado com ${modosParticipacao.size} modos de participação na sessão: ${modosParticipacaoStr}.`}
                placement="top"
            >
                <Warning style={{ fill: 'darkorange', marginLeft: '7px', verticalAlign: 'sub', fontSize: '22px' }} />
            </Tooltip>
        );
    }

    renderAlertaDuplicata() {
        const cadastrosAdvogadosDuplicados = this.verificaDuplicata();

        if (cadastrosAdvogadosDuplicados.length === 0) {
            return null;
        }

        const flexaoPlural = cadastrosAdvogadosDuplicados.length > 1 ? 's' : '';

        return (
            <Tooltip
                title={
                    <Typography>
                        $
                        {`Atenção! Há ${cadastrosAdvogadosDuplicados} cadastros de advogado${flexaoPlural} duplicados.`}
                    </Typography>
                }
                placement="top"
            >
                <Warning style={{ fill: 'darkorange', marginLeft: '7px', verticalAlign: 'sub', fontSize: '22px' }} />
            </Tooltip>
        );
    }

    verificaDuplicata() {
        const duplicata = [];
        const advogadosIguais = [];

        this.advogados.forEach((advogado) => {
            const chaveAdvogado = this.salaSessaoStore.getRepresentanteKey(advogado);

            if (duplicata.includes(chaveAdvogado)) {
                advogadosIguais.push(advogado.nome);
            } else {
                duplicata.push(chaveAdvogado);
            }
        });

        return advogadosIguais;
    }

    renderVisualizacaoAgrupada() {
        return (
            <Table
                stickyHeader
                aria-label="Tabela de advogados da sessão"
            >
                <TableHead style={{ overflowY: 'hidden', maxHeight: '150px' }}>
                    <TableRow>
                        <TableCell
                            align="center"
                            style={{
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                minWidth: LARGURA_COLUNA_NOME_ADVOGADO
                            }}
                        >
                            Advogado
                        </TableCell>
                        <TableCell
                            align="center"
                            colSpan={3}
                            style={{ fontWeight: 'bold', fontSize: '14px' }}
                        >
                            Indicação de presença
                        </TableCell>
                        <TableCell
                            align="center"
                            colSpan={3}
                            style={{ fontWeight: 'bold', fontSize: '14px' }}
                        >
                            Modo de participação
                        </TableCell>
                        {this.isComponenteHabilitado('AlterarEstadoSustentacaoOral') && (
                            <TableCell
                                align="center"
                                colSpan={4}
                                style={{ fontWeight: 'bold', fontSize: '14px' }}
                            >
                                Estado sustentação oral
                            </TableCell>
                        )}
                    </TableRow>
                    <TableRow>
                        <TableCell
                            align="left"
                            style={{
                                borderTop: ESTILO_BORDA,
                                fontWeight: 'bold',
                                minWidth: LARGURA_COLUNA_NOME_ADVOGADO
                            }}
                        >
                            NOME DO ADVOGADO
                            {this.renderAlertaDuplicata()}
                        </TableCell>
                        <TableCell
                            align="center"
                            style={{
                                borderTop: ESTILO_BORDA,
                                fontWeight: 'bold',
                                fontSize: FONTE_COLUNAS_CONTROLE,
                                width: LARGURA_COLUNAS_PRESENCA
                            }}
                        >
                            {SITUACAO_INSCRICAO_ADVOGADO.INSCRITO.descricaoPresencaCurta.toUpperCase()}
                        </TableCell>
                        <TableCell
                            align="center"
                            style={{
                                borderTop: ESTILO_BORDA,
                                fontWeight: 'bold',
                                fontSize: FONTE_COLUNAS_CONTROLE,
                                width: LARGURA_COLUNAS_PRESENCA
                            }}
                        >
                            {SITUACAO_INSCRICAO_ADVOGADO.PRESENTE.descricaoPresencaCurta.toUpperCase()}
                        </TableCell>
                        <TableCell
                            align="center"
                            style={{
                                borderTop: ESTILO_BORDA,
                                fontWeight: 'bold',
                                fontSize: FONTE_COLUNAS_CONTROLE,
                                width: LARGURA_COLUNAS_PRESENCA
                            }}
                        >
                            {SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.descricaoPresencaCurta.toUpperCase()}
                        </TableCell>
                        <TableCell
                            align="center"
                            style={{
                                borderTop: ESTILO_BORDA,
                                fontWeight: 'bold',
                                fontSize: FONTE_COLUNAS_CONTROLE,
                                width: LARGURA_COLUNAS_MODOS_PARTICIPACAO
                            }}
                        >
                            {MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.descricao.toUpperCase()}
                        </TableCell>
                        <TableCell
                            align="center"
                            style={{
                                borderTop: ESTILO_BORDA,
                                fontWeight: 'bold',
                                fontSize: FONTE_COLUNAS_CONTROLE,
                                width: LARGURA_COLUNAS_MODOS_PARTICIPACAO
                            }}
                        >
                            {MODO_PARTICIPACAO_ADVOGADO.PRESENCIAL.descricao.toUpperCase()}
                        </TableCell>
                        <TableCell
                            align="center"
                            style={{
                                borderTop: ESTILO_BORDA,
                                fontWeight: 'bold',
                                fontSize: FONTE_COLUNAS_CONTROLE,
                                width: LARGURA_COLUNAS_MODOS_PARTICIPACAO
                            }}
                        >
                            {MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.descricao.toUpperCase()}
                        </TableCell>
                        {this.isComponenteHabilitado('AlterarEstadoSustentacaoOral') && (
                            <>
                                <TableCell
                                    align="center"
                                    style={{
                                        borderTop: ESTILO_BORDA,
                                        fontWeight: 'bold',
                                        fontSize: FONTE_COLUNAS_CONTROLE,
                                        width: LARGURA_COLUNAS_ESTADO_SUSTENTACAO
                                    }}
                                >
                                    SUSTENTOU
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{
                                        borderTop: ESTILO_BORDA,
                                        fontWeight: 'bold',
                                        fontSize: FONTE_COLUNAS_CONTROLE,
                                        width: LARGURA_COLUNAS_ESTADO_SUSTENTACAO
                                    }}
                                >
                                    NÃO SUST.
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{
                                        borderTop: ESTILO_BORDA,
                                        fontWeight: 'bold',
                                        fontSize: FONTE_COLUNAS_CONTROLE,
                                        width: LARGURA_COLUNAS_ESTADO_SUSTENTACAO
                                    }}
                                >
                                    ASSEG.
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{
                                        borderTop: ESTILO_BORDA,
                                        fontWeight: 'bold',
                                        fontSize: FONTE_COLUNAS_CONTROLE,
                                        width: LARGURA_COLUNAS_ESTADO_SUSTENTACAO
                                    }}
                                >
                                    NÃO ASSEG.
                                </TableCell>
                            </>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody style={{ overflowY: 'auto', maxHeight: '400px' }}>
                    {this.advogados.map((adv, index) => {
                        const chaveAdvogado = this.salaSessaoStore.getRepresentanteKey(adv);
                        const { presencas, modosParticipacao } =
                            this.salaSessaoStore.getPresencasModosParticipacao(chaveAdvogado);

                        return (
                            <TableRow key={chaveAdvogado}>
                                <TableCell
                                    align="left"
                                    style={{ minWidth: LARGURA_COLUNA_NOME_ADVOGADO }}
                                >
                                    {adv.nome}
                                    {this.renderTiposInscricoesAdvogado(adv)}
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{ borderLeft: ESTILO_BORDA, padding: '4px' }}
                                >
                                    <Radio
                                        size="small"
                                        key={`${chaveAdvogado}|${SITUACAO_INSCRICAO_ADVOGADO.INSCRITO.codigo}`}
                                        value={SITUACAO_INSCRICAO_ADVOGADO.INSCRITO.codigo}
                                        onChange={this.handleChangePresenca.bind(
                                            this,
                                            SITUACAO_INSCRICAO_ADVOGADO.INSCRITO.codigo,
                                            index
                                        )}
                                        checked={
                                            presencas.size === 1 &&
                                            adv.presente === SITUACAO_INSCRICAO_ADVOGADO.INSCRITO.codigo
                                        }
                                    />
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{ padding: '4px' }}
                                >
                                    <Radio
                                        size="small"
                                        key={`${chaveAdvogado}|${SITUACAO_INSCRICAO_ADVOGADO.PRESENTE.codigo}`}
                                        value={SITUACAO_INSCRICAO_ADVOGADO.PRESENTE.codigo}
                                        onChange={this.handleChangePresenca.bind(
                                            this,
                                            SITUACAO_INSCRICAO_ADVOGADO.PRESENTE.codigo,
                                            index
                                        )}
                                        checked={
                                            presencas.size === 1 &&
                                            adv.presente === SITUACAO_INSCRICAO_ADVOGADO.PRESENTE.codigo
                                        }
                                    />
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{ padding: '4px' }}
                                >
                                    <Radio
                                        size="small"
                                        key={`${chaveAdvogado}|${SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo}`}
                                        value={SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo}
                                        onChange={this.handleChangePresenca.bind(
                                            this,
                                            SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo,
                                            index
                                        )}
                                        checked={
                                            presencas.size === 1 &&
                                            adv.presente === SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo
                                        }
                                    />
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{ borderLeft: ESTILO_BORDA, padding: '4px' }}
                                >
                                    <Radio
                                        size="small"
                                        key={`${chaveAdvogado}|${MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.codigo}`}
                                        value={MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.codigo}
                                        onChange={this.handleChangeParticipacao.bind(
                                            this,
                                            MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.codigo,
                                            index
                                        )}
                                        checked={
                                            modosParticipacao.size === 1 &&
                                            adv.modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.codigo
                                        }
                                    />
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{ padding: '4px' }}
                                >
                                    <Radio
                                        size="small"
                                        key={`${chaveAdvogado}|${MODO_PARTICIPACAO_ADVOGADO.PRESENCIAL.codigo}`}
                                        value={MODO_PARTICIPACAO_ADVOGADO.PRESENCIAL.codigo}
                                        onChange={this.handleChangeParticipacao.bind(
                                            this,
                                            MODO_PARTICIPACAO_ADVOGADO.PRESENCIAL.codigo,
                                            index
                                        )}
                                        checked={
                                            modosParticipacao.size === 1 &&
                                            adv.modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.PRESENCIAL.codigo
                                        }
                                    />
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{ padding: '4px' }}
                                >
                                    <Radio
                                        size="small"
                                        key={`${chaveAdvogado}|${MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo}`}
                                        value={MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo}
                                        onChange={this.handleChangeParticipacao.bind(
                                            this,
                                            MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo,
                                            index
                                        )}
                                        checked={
                                            modosParticipacao.size === 1 &&
                                            adv.modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo
                                        }
                                    />
                                </TableCell>
                                {this.isComponenteHabilitado('AlterarEstadoSustentacaoOral') && (
                                    <>
                                        <TableCell
                                            align="center"
                                            style={{ borderLeft: ESTILO_BORDA, padding: '4px' }}
                                        >
                                            <Radio
                                                size="small"
                                                key={`${chaveAdvogado}|${ESTADO_SUSTENTACAO_ORAL.SUSTENTOU.codigo}`}
                                                value={ESTADO_SUSTENTACAO_ORAL.SUSTENTOU.codigo}
                                                onChange={this.handleChangeEstadoSustentacaoOral.bind(
                                                    this,
                                                    ESTADO_SUSTENTACAO_ORAL.SUSTENTOU.codigo,
                                                    index
                                                )}
                                                checked={
                                                    adv.estadoSustentacaoOral?.id ===
                                                    ESTADO_SUSTENTACAO_ORAL.SUSTENTOU.codigo
                                                }
                                            />
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            style={{ padding: '4px' }}
                                        >
                                            <Radio
                                                size="small"
                                                key={`${chaveAdvogado}|${ESTADO_SUSTENTACAO_ORAL.NAO_SUSTENTOU.codigo}`}
                                                value={ESTADO_SUSTENTACAO_ORAL.NAO_SUSTENTOU.codigo}
                                                onChange={this.handleChangeEstadoSustentacaoOral.bind(
                                                    this,
                                                    ESTADO_SUSTENTACAO_ORAL.NAO_SUSTENTOU.codigo,
                                                    index
                                                )}
                                                checked={
                                                    adv.estadoSustentacaoOral?.id ===
                                                    ESTADO_SUSTENTACAO_ORAL.NAO_SUSTENTOU.codigo
                                                }
                                            />
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            style={{ padding: '4px' }}
                                        >
                                            <Radio
                                                size="small"
                                                key={`${chaveAdvogado}|${ESTADO_SUSTENTACAO_ORAL.TEVE_ASSEGURADA.codigo}`}
                                                value={ESTADO_SUSTENTACAO_ORAL.TEVE_ASSEGURADA.codigo}
                                                onChange={this.handleChangeEstadoSustentacaoOral.bind(
                                                    this,
                                                    ESTADO_SUSTENTACAO_ORAL.TEVE_ASSEGURADA.codigo,
                                                    index
                                                )}
                                                checked={
                                                    adv.estadoSustentacaoOral?.id ===
                                                    ESTADO_SUSTENTACAO_ORAL.TEVE_ASSEGURADA.codigo
                                                }
                                            />
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            style={{ padding: '4px' }}
                                        >
                                            <Radio
                                                size="small"
                                                key={`${chaveAdvogado}|${ESTADO_SUSTENTACAO_ORAL.NAO_TEVE_ASSEGURADA.codigo}`}
                                                value={ESTADO_SUSTENTACAO_ORAL.NAO_TEVE_ASSEGURADA.codigo}
                                                onChange={this.handleChangeEstadoSustentacaoOral.bind(
                                                    this,
                                                    ESTADO_SUSTENTACAO_ORAL.NAO_TEVE_ASSEGURADA.codigo,
                                                    index
                                                )}
                                                checked={
                                                    adv.estadoSustentacaoOral?.id ===
                                                    ESTADO_SUSTENTACAO_ORAL.NAO_TEVE_ASSEGURADA.codigo
                                                }
                                            />
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        );
    }

    renderVisualizacaoUnitaria() {
        return (
            <Table
                stickyHeader
                aria-label="Tabela de advogados da sessão"
            >
                <TableHead style={{ overflowY: 'hidden', maxHeight: '150px' }}>
                    <TableRow>
                        <TableCell
                            align="left"
                            style={{
                                borderTop: ESTILO_BORDA,
                                fontWeight: 'bold'
                            }}
                        >
                            NOME DO ADVOGADO
                        </TableCell>
                        <TableCell
                            align="left"
                            style={{
                                borderTop: ESTILO_BORDA,
                                fontWeight: 'bold'
                            }}
                        >
                            MODO DE PARTICIPAÇÃO
                        </TableCell>
                        <TableCell
                            align="left"
                            style={{
                                borderTop: ESTILO_BORDA,
                                fontWeight: 'bold'
                            }}
                        >
                            NÚMERO DO PROCESSO
                        </TableCell>
                        <TableCell
                            align="left"
                            style={{
                                borderTop: ESTILO_BORDA,
                                fontWeight: 'bold'
                            }}
                        >
                            NOME DA PARTE
                        </TableCell>
                        <TableCell
                            align="center"
                            style={{
                                borderTop: ESTILO_BORDA,
                                fontWeight: 'bold',
                                width: LARGURA_COLUNAS_PRESENCA_VISAO_UNITARIA
                            }}
                            colSpan={3}
                        >
                            <Box
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: 16,
                                    mb: 1,
                                    pb: 1,
                                    borderBottom: ESTILO_BORDA
                                }}
                            >
                                Indicação de presença
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2
                                }}
                            >
                                <Typography sx={{ flex: 1, fontWeight: 'bold', fontSize: 14 }}>
                                    {SITUACAO_INSCRICAO_ADVOGADO.INSCRITO.descricaoPresencaCurta}
                                </Typography>
                                <Typography sx={{ flex: 1, fontWeight: 'bold', fontSize: 14 }}>
                                    {SITUACAO_INSCRICAO_ADVOGADO.PRESENTE.descricaoPresencaCurta}
                                </Typography>
                                <Typography sx={{ flex: 1, fontWeight: 'bold', fontSize: 14 }}>
                                    {SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.descricaoPresencaCurta}
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell
                            align="center"
                            style={{
                                borderTop: ESTILO_BORDA,
                                fontWeight: 'bold',
                                width: LARGURA_COLUNAS_MODOS_PARTICIPACAO_VISAO_UNITARIA
                            }}
                            colSpan={3}
                        >
                            <Box
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: 16,
                                    mb: 1,
                                    pb: 1,
                                    borderBottom: ESTILO_BORDA
                                }}
                            >
                                Modo de participação
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2
                                }}
                            >
                                <Typography sx={{ flex: 1, fontWeight: 'bold', fontSize: 14 }}>
                                    {MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.descricao}
                                </Typography>
                                <Typography sx={{ flex: 1, fontWeight: 'bold', fontSize: 14 }}>
                                    {MODO_PARTICIPACAO_ADVOGADO.PRESENCIAL.descricao}
                                </Typography>
                                <Typography sx={{ flex: 1, fontWeight: 'bold', fontSize: 14 }}>
                                    {MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.descricao}
                                </Typography>
                            </Box>
                        </TableCell>
                        {this.isComponenteHabilitado('AlterarEstadoSustentacaoOral') && (
                            <TableCell
                                align="center"
                                style={{
                                    borderTop: ESTILO_BORDA,
                                    fontWeight: 'bold'
                                }}
                                colSpan={4}
                            >
                                <Box
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: 16,
                                        mb: 1,
                                        pb: 1,
                                        borderBottom: ESTILO_BORDA
                                    }}
                                >
                                    Estado sustentação oral
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 2
                                    }}
                                >
                                    <Typography sx={{ flex: 1, fontWeight: 'bold', fontSize: 14 }}>
                                        Sustentou
                                    </Typography>
                                    <Typography sx={{ flex: 1, fontWeight: 'bold', fontSize: 14 }}>
                                        Não sust.
                                    </Typography>
                                    <Typography sx={{ flex: 1, fontWeight: 'bold', fontSize: 14 }}>
                                        Assegurada
                                    </Typography>
                                    <Typography sx={{ flex: 1, fontWeight: 'bold', fontSize: 14 }}>
                                        Não asseg.
                                    </Typography>
                                </Box>
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody style={{ overflowY: 'auto', maxHeight: '400px' }}>
                    {Object.keys(this.inscricoes).map((chaveInscricao) => {
                        const inscricao = this.inscricoes[chaveInscricao];

                        return (
                            <TableRow key={chaveInscricao}>
                                <TableCell align="left">{inscricao.advogado.nome}</TableCell>
                                <TableCell align="center">
                                    {this.renderTipoInscricaoAdvogadoUnitario(
                                        inscricao.advogado,
                                        inscricao.presente,
                                        inscricao.modoParticipacao
                                    )}
                                </TableCell>
                                <TableCell align="left">{inscricao.numeroCompletoProcesso}</TableCell>
                                <TableCell align="left">{inscricao.nomeDaParte}</TableCell>
                                <TableCell
                                    align="center"
                                    style={{ borderLeft: ESTILO_BORDA }}
                                >
                                    <Radio
                                        key={`${chaveInscricao}|${SITUACAO_INSCRICAO_ADVOGADO.INSCRITO.codigo}`}
                                        value={SITUACAO_INSCRICAO_ADVOGADO.INSCRITO.codigo}
                                        onChange={this.handleChangePresencaUnitaria.bind(
                                            this,
                                            SITUACAO_INSCRICAO_ADVOGADO.INSCRITO.codigo,
                                            inscricao
                                        )}
                                        checked={inscricao.presente === SITUACAO_INSCRICAO_ADVOGADO.INSCRITO.codigo}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Radio
                                        key={`${chaveInscricao}|${SITUACAO_INSCRICAO_ADVOGADO.PRESENTE.codigo}`}
                                        value={SITUACAO_INSCRICAO_ADVOGADO.PRESENTE.codigo}
                                        onChange={this.handleChangePresencaUnitaria.bind(
                                            this,
                                            SITUACAO_INSCRICAO_ADVOGADO.PRESENTE.codigo,
                                            inscricao
                                        )}
                                        checked={inscricao.presente === SITUACAO_INSCRICAO_ADVOGADO.PRESENTE.codigo}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Radio
                                        key={`${chaveInscricao}|${SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo}`}
                                        value={SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo}
                                        onChange={this.handleChangePresencaUnitaria.bind(
                                            this,
                                            SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo,
                                            inscricao
                                        )}
                                        checked={inscricao.presente === SITUACAO_INSCRICAO_ADVOGADO.AUSENTE.codigo}
                                    />
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{ borderLeft: ESTILO_BORDA }}
                                >
                                    <Radio
                                        key={`${chaveInscricao}|${MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.codigo}`}
                                        value={MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.codigo}
                                        onChange={this.handleChangeParticipacaoUnitaria.bind(
                                            this,
                                            MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.codigo,
                                            inscricao
                                        )}
                                        checked={
                                            inscricao.modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.codigo
                                        }
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Radio
                                        key={`${chaveInscricao}|${MODO_PARTICIPACAO_ADVOGADO.PRESENCIAL.codigo}`}
                                        value={MODO_PARTICIPACAO_ADVOGADO.PRESENCIAL.codigo}
                                        onChange={this.handleChangeParticipacaoUnitaria.bind(
                                            this,
                                            MODO_PARTICIPACAO_ADVOGADO.PRESENCIAL.codigo,
                                            inscricao
                                        )}
                                        checked={
                                            inscricao.modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.PRESENCIAL.codigo
                                        }
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Radio
                                        key={`${chaveInscricao}|${MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo}`}
                                        value={MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo}
                                        onChange={this.handleChangeParticipacaoUnitaria.bind(
                                            this,
                                            MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo,
                                            inscricao
                                        )}
                                        checked={
                                            inscricao.modoParticipacao ===
                                            MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo
                                        }
                                    />
                                </TableCell>
                                {this.isComponenteHabilitado('AlterarEstadoSustentacaoOral') && (
                                    <>
                                        <TableCell
                                            align="center"
                                            style={{ borderLeft: ESTILO_BORDA }}
                                        >
                                            <Radio
                                                key={`${chaveInscricao}|${ESTADO_SUSTENTACAO_ORAL.SUSTENTOU.codigo}`}
                                                value={ESTADO_SUSTENTACAO_ORAL.SUSTENTOU.codigo}
                                                onChange={this.handleChangeEstadoSustentacaoOralUnitaria.bind(
                                                    this,
                                                    ESTADO_SUSTENTACAO_ORAL.SUSTENTOU.codigo,
                                                    inscricao
                                                )}
                                                checked={
                                                    inscricao.estadoSustentacaoOral?.id ===
                                                    ESTADO_SUSTENTACAO_ORAL.SUSTENTOU.codigo
                                                }
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Radio
                                                key={`${chaveInscricao}|${ESTADO_SUSTENTACAO_ORAL.NAO_SUSTENTOU.codigo}`}
                                                value={ESTADO_SUSTENTACAO_ORAL.NAO_SUSTENTOU.codigo}
                                                onChange={this.handleChangeEstadoSustentacaoOralUnitaria.bind(
                                                    this,
                                                    ESTADO_SUSTENTACAO_ORAL.NAO_SUSTENTOU.codigo,
                                                    inscricao
                                                )}
                                                checked={
                                                    inscricao.estadoSustentacaoOral?.id ===
                                                    ESTADO_SUSTENTACAO_ORAL.NAO_SUSTENTOU.codigo
                                                }
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Radio
                                                key={`${chaveInscricao}|${ESTADO_SUSTENTACAO_ORAL.TEVE_ASSEGURADA.codigo}`}
                                                value={ESTADO_SUSTENTACAO_ORAL.TEVE_ASSEGURADA.codigo}
                                                onChange={this.handleChangeEstadoSustentacaoOralUnitaria.bind(
                                                    this,
                                                    ESTADO_SUSTENTACAO_ORAL.TEVE_ASSEGURADA.codigo,
                                                    inscricao
                                                )}
                                                checked={
                                                    inscricao.estadoSustentacaoOral?.id ===
                                                    ESTADO_SUSTENTACAO_ORAL.TEVE_ASSEGURADA.codigo
                                                }
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Radio
                                                key={`${chaveInscricao}|${ESTADO_SUSTENTACAO_ORAL.NAO_TEVE_ASSEGURADA.codigo}`}
                                                value={ESTADO_SUSTENTACAO_ORAL.NAO_TEVE_ASSEGURADA.codigo}
                                                onChange={this.handleChangeEstadoSustentacaoOralUnitaria.bind(
                                                    this,
                                                    ESTADO_SUSTENTACAO_ORAL.NAO_TEVE_ASSEGURADA.codigo,
                                                    inscricao
                                                )}
                                                checked={
                                                    inscricao.estadoSustentacaoOral?.id ===
                                                    ESTADO_SUSTENTACAO_ORAL.NAO_TEVE_ASSEGURADA.codigo
                                                }
                                            />
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        );
    }

    render() {
        if (!this.isComponenteHabilitado('ModalControleAdvogados')) {
            return null;
        }

        if (this.gerandoArquivo) {
            return <CircularProgress size="24px" />;
        }

        return (
            <>
                <Tooltip
                    title={<Typography>Controlar advogados da sessão</Typography>}
                    placement="top-start"
                >
                    <Box
                        style={{ display: 'flex', margin: '8px', cursor: 'pointer', fill: 'white' }}
                        className="corBotaoAmarelo"
                        onClick={this.abrir}
                    >
                        <Group />
                    </Box>
                </Tooltip>
                {this.aberto && this.advogados.length > 0 && (
                    <Dialog
                        open={this.aberto}
                        onClose={this.fechar}
                        maxWidth="xl"
                    >
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                                Controle de advogados com participação em sessão presencial/telepresencial/híbrida
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={this.visualizacaoAgrupada}
                                                onChange={this.handleChangeVisualizacaoAgrupada}
                                            />
                                        }
                                        label="Visualização agrupada"
                                    />
                                </FormGroup>
                            </Box>
                        </DialogTitle>
                        <DialogContent
                            sx={{ mt: '20px' }}
                            style={{ display: 'flex', height: '550px' }}
                        >
                            <TableContainer component={Paper}>
                                {this.visualizacaoAgrupada
                                    ? this.renderVisualizacaoAgrupada()
                                    : this.renderVisualizacaoUnitaria()}
                            </TableContainer>
                        </DialogContent>
                        <DialogActions sx={{ mr: 2, mb: 1 }}>
                            <Stack
                                direction="row"
                                spacing={2}
                            >
                                <Button
                                    color="secondary"
                                    variant="outlined"
                                    style={{ alignItems: 'center' }}
                                    endIcon={<Close />}
                                    onClick={this.fechar}
                                >
                                    FECHAR
                                </Button>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    endIcon={<FontAwesomeIcon icon={faFileExport} />}
                                    onClick={this.handleClickExportarLista}
                                    sx={{ ml: 2 }}
                                >
                                    EXPORTAR PARA ARQUIVO CSV
                                </Button>
                            </Stack>
                        </DialogActions>
                    </Dialog>
                )}
            </>
        );
    }
}

export default inject('stores')(observer(ModalControleAdvogados));
